import {
    getScopeStatus,
    IApiAxiosError,
    ILibraryListScope,
    IOrganizationContextResponse,
    IScope,
    organizationQueryKeys,
    readScopesEnabled,
    ScopeStatus,
} from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { TASK_REFETCH_INTERVAL } from "@/config/constants";
import { useAuth } from "@/contexts/auth/useAuth";
import { useProcessingScopes } from "@/contexts/frontend/processing-scopes/useProcessingScopes";
import { Close } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";

type ProcessingScope = IScope | ILibraryListScope;

function ScopeCompletionActions({
    snackbarId,
    onView,
}: {
    snackbarId: SnackbarKey;
    onView: () => void;
}) {
    return (
        <>
            <Button
                type="button"
                onClick={onView}
                variant="contained"
                color="primary"
                size="small"
            >
                View Scope
            </Button>
            <IconButton
                aria-label="Dismiss scope notification"
                onClick={() => closeSnackbar(snackbarId)}
                size="small"
                color="inherit"
                sx={{ ml: 1 }}
            >
                <Close fontSize="small" />
            </IconButton>
        </>
    );
}

interface IScopeStatusPollerProps {
    scope: ProcessingScope;
}

const ScopeStatusPoller = ({ scope }: IScopeStatusPollerProps) => {
    const { removeProcessingScope } = useProcessingScopes();
    const { authenticated } = useAuth();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const {
        data: scopeStatus,
        isError,
        error,
    } = useQuery({
        queryKey: ["frontend-scope-status", scope.id],
        queryFn: () => getScopeStatus(scope.id),
        refetchInterval: authenticated ? TASK_REFETCH_INTERVAL : false,
        enabled: authenticated,
    });

    const handleViewScope = useCallback(
        (snackbarId: SnackbarKey) => {
            navigate(`/organizations/scopes/${scope.id}`);
            closeSnackbar(snackbarId);
        },
        [navigate, scope.id]
    );

    useEffect(() => {
        if (isError) {
            const apiError = error as IApiAxiosError;

            // If unauthorized, do not show an error or remove the scope
            if (apiError.response?.status === 401) {
                return;
            }

            removeProcessingScope(scope.id);

            enqueueSnackbar(
                `An error occurred while fetching status for ${scope.document_title}.`,
                {
                    variant: "error",
                    action: (snackbarId: SnackbarKey) => (
                        <IconButton
                            onClick={() => closeSnackbar(snackbarId)}
                            size="small"
                            color="inherit"
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    ),
                    preventDuplicate: true,
                }
            );
        }
    }, [isError, error, removeProcessingScope, scope.id, scope]);

    useEffect(() => {
        if (
            scopeStatus &&
            scopeStatus.status !== ScopeStatus.PROCESSING &&
            scopeStatus.status !== ScopeStatus.PENDING
        ) {
            removeProcessingScope(scope.id);

            queryClient.invalidateQueries({
                queryKey: ["frontend-scope", scope.id.toString()],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-organization-recent-scopes"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-list"],
            });

            queryClient.removeQueries({
                queryKey: ["frontend-organization-recent-scopes"],
            });
            queryClient.removeQueries({
                queryKey: ["scope-topics-with-clauses", scope.id],
            });
            queryClient.removeQueries({
                queryKey: ["frontend-scope-overview", scope.id],
            });
            queryClient.removeQueries({
                queryKey: ["scope-extracted-fields", scope.id],
            });

            if (scopeStatus.status === ScopeStatus.COMPLETE) {
                const organizationContext =
                    queryClient.getQueryData<IOrganizationContextResponse>(
                        organizationQueryKeys.context
                    );
                const scopesEnabled = readScopesEnabled(
                    organizationContext?.organization_settings
                );

                if (scopesEnabled) {
                    const viewScopeAction = (snackbarId: SnackbarKey) => (
                        <ScopeCompletionActions
                            snackbarId={snackbarId}
                            onView={() => handleViewScope(snackbarId)}
                        />
                    );

                    enqueueSnackbar(
                        `${scope.document_title} has been processed and is ready to review.`,
                        {
                            variant: "success",
                            action: viewScopeAction,
                            persist: true,
                            preventDuplicate: true,
                        }
                    );
                }
            }

            if (scopeStatus.status === ScopeStatus.FAILED) {
                enqueueSnackbar(`${scope.document_title} failed to process.`, {
                    variant: "error",
                    action: (snackbarId: SnackbarKey) => (
                        <IconButton
                            onClick={() => closeSnackbar(snackbarId)}
                            size="small"
                            color="inherit"
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    ),
                });
            }
        }
    }, [
        scopeStatus,
        removeProcessingScope,
        scope.id,
        queryClient,
        scope,
        handleViewScope,
    ]);

    return null;
};

export const ProcessingScopesManager = () => {
    const { processingScopes } = useProcessingScopes();

    const scopesToProcess = useMemo(
        () => Object.values(processingScopes),
        [processingScopes]
    );

    return (
        <>
            {scopesToProcess.map((scope) => (
                <ScopeStatusPoller key={scope.id} scope={scope} />
            ))}
        </>
    );
};
